import type { LucideIcon } from 'lucide-react';
import type { ButtonHTMLAttributes, ReactNode } from 'react';
import { Link } from 'react-router-dom';

import styles from './Button.module.css';
import { Icon } from './Icon';

export type ButtonVariant =
  'primary' | 'secondary' | 'outline' | 'ghost' | 'inverse' | 'ghostOnInk';
export type ButtonSize = 'sm' | 'md' | 'lg';

type CommonProps = {
  children: ReactNode;
  variant?: ButtonVariant;
  size?: ButtonSize;
  shape?: 'pill' | 'soft';
  iconLeft?: LucideIcon;
  iconRight?: LucideIcon;
  fullWidth?: boolean;
  loading?: boolean;
  className?: string;
};

type AsButton = CommonProps &
  Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children' | 'className'> & {
    to?: undefined;
    href?: undefined;
  };
type AsRouterLink = CommonProps & {
  to: string;
  href?: undefined;
  onClick?: () => void;
  replace?: boolean;
};
type AsExternal = CommonProps & {
  href: string;
  to?: undefined;
  target?: string;
  onClick?: () => void;
};

export type ButtonProps = AsButton | AsRouterLink | AsExternal;

const iconSize = { sm: 16, md: 18, lg: 20 } as const;

export function Button(props: ButtonProps) {
  const {
    children,
    variant = 'primary',
    size = 'md',
    shape = 'pill',
    iconLeft,
    iconRight,
    fullWidth,
    loading,
    className,
  } = props;
  const classes = [
    styles.button,
    styles[size],
    styles[variant],
    shape === 'soft' ? styles.soft : '',
    fullWidth ? styles.fullWidth : '',
    className ?? '',
  ]
    .filter(Boolean)
    .join(' ');
  const content = (
    <>
      {loading ? (
        <span className={styles.spinner} aria-hidden="true" />
      ) : (
        iconLeft && <Icon icon={iconLeft} size={iconSize[size]} />
      )}
      {children}
      {iconRight && <Icon icon={iconRight} size={iconSize[size]} />}
    </>
  );

  if ('to' in props && props.to !== undefined) {
    return (
      <Link to={props.to} replace={props.replace} className={classes} onClick={props.onClick}>
        {content}
      </Link>
    );
  }
  if ('href' in props && props.href !== undefined) {
    const external = /^https?:/.test(props.href);
    return (
      <a
        href={props.href}
        className={classes}
        onClick={props.onClick}
        target={props.target ?? (external ? '_blank' : undefined)}
        rel={external ? 'noopener noreferrer' : undefined}
      >
        {content}
      </a>
    );
  }
  const {
    variant: _v,
    size: _s,
    shape: _sh,
    iconLeft: _il,
    iconRight: _ir,
    fullWidth: _fw,
    loading: _l,
    className: _c,
    children: _ch,
    to: _to,
    href: _href,
    type = 'button',
    disabled,
    ...rest
  } = props as AsButton;
  return (
    <button
      {...rest}
      type={type}
      className={classes}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
    >
      {content}
    </button>
  );
}
